import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * SceneContext — drives the 'Emergent Architectural Interface'.
 *
 * - Holds an ordered list of pre-generated blueprint scenes.
 * - `mutate()` advances to the next scene (called on global click).
 * - `setSceneById(id)` jumps to a specific scene (called by chapter scroll).
 * - Crossfade is handled by <GlobalCanvas/> reading `sceneIndex`.
 */

export const SCENES = [
  { id: 'command-center', src: '/scenes/scene-00-command-center.webp', accent: '#60A5FA', mood: 'Master Plan' },
  { id: 'statement',      src: '/scenes/scene-01-statement.webp',      accent: '#3B82F6', mood: 'Stillness' },
  { id: 'protocols',      src: '/scenes/scene-02-protocols.webp',      accent: '#22D3EE', mood: 'Three Modules' },
  { id: 'origin',         src: '/scenes/scene-03-origin.webp',         accent: '#A5B4FC', mood: 'Atelier' },
  { id: 'field-ops',      src: '/scenes/scene-04-field-ops.webp',      accent: '#38BDF8', mood: 'Field Ops' },
  { id: 'arsenal',        src: '/scenes/scene-05-arsenal.webp',        accent: '#818CF8', mood: 'Arsenal' },
  { id: 'signal-hub',     src: '/scenes/scene-06-signal-hub.webp',     accent: '#34D399', mood: 'Live Signal' },
  { id: 'mutation',       src: '/scenes/scene-07-mutation.webp',       accent: '#F59E0B', mood: 'Second Draft' },
];

const SceneContext = createContext(null);

export function SceneProvider({ children }) {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [mutationCount, setMutationCount] = useState(0);
  const lastSetByScrollRef = useRef(0);

  const mutate = useCallback(() => {
    setSceneIndex((i) => (i + 1) % SCENES.length);
    setMutationCount((c) => c + 1);
  }, []);

  const setSceneById = useCallback((id, source = 'manual') => {
    const idx = SCENES.findIndex((s) => s.id === id);
    if (idx === -1) return;
    if (source === 'scroll') lastSetByScrollRef.current = Date.now();
    setSceneIndex(idx);
  }, []);

  // Expose CSS variable for accent color so any component can theme to active scene
  useEffect(() => {
    const accent = SCENES[sceneIndex].accent;
    document.documentElement.style.setProperty('--scene-accent', accent);
  }, [sceneIndex]);

  const value = {
    scenes: SCENES,
    sceneIndex,
    scene: SCENES[sceneIndex],
    mutate,
    setSceneById,
    mutationCount,
  };

  return <SceneContext.Provider value={value}>{children}</SceneContext.Provider>;
}

export function useScene() {
  const ctx = useContext(SceneContext);
  if (!ctx) throw new Error('useScene must be used inside <SceneProvider>');
  return ctx;
}

/**
 * useChapterScene — registers an element so scrolling it into view
 * activates the given scene id.
 *
 * Usage:
 *   const ref = useChapterScene('protocols');
 *   <section ref={ref}>...</section>
 */
export function useChapterScene(id) {
  const { setSceneById } = useContext(SceneContext) || {};
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !setSceneById) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.45) {
            setSceneById(id, 'scroll');
          }
        });
      },
      { threshold: [0, 0.45, 0.7, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [id, setSceneById]);

  return ref;
}
